import { t } from "@lingui/macro";
import { CheckCircle, Fingerprint } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

import { useAuthProviders } from "@/client/services/auth/providers";

// Import the logo components
const GithubLogo = ({ size = 20, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="currentColor"
    className={className}
    viewBox="0 0 1792 1792"
  >
    <path d="M896 128q209 0 385.5 103t279.5 279.5 103 385.5q0 251-146.5 451.5t-378.5 277.5q-27 5-40-7t-13-30q0-3 .5-76.5t.5-134.5q0-97-52-142 57-6 102.5-18t94-39 81-66.5 53-105 20.5-150.5q0-119-79-206 37-91-8-204-28-9-81 11t-92 44l-38 24q-93-26-192-26t-192 26q-16-11-42.5-27t-83.5-38.5-85-13.5q-45 113-8 204-79 87-79 206 0 85 20.5 150t52.5 105 80.5 67 94 39 102.5 18q-39 36-49 103-21 10-45 15t-57 5-65.5-21.5-55.5-62.5q-19-32-48.5-52t-49.5-24l-20-3q-21 0-29 4.5t-5 11.5 9 14 13 12l7 5q22 10 43.5 38t31.5 51l10 23q13 38 44 61.5t67 30 69.5 7 55.5-3.5l23-4q0 38 .5 88.5t.5 54.5q0 18-13 30t-40 7q-232-77-378.5-277.5t-146.5-451.5q0-209 103-385.5t279.5-279.5 385.5-103zm-477 1103q3-7-7-12-10-3-13 2-3 7 7 12 9 6 13-2zm31 34q7-5-2-16-10-9-16-3-7 5 2 16 10 10 16 3zm30 45q9-7 0-19-8-13-17-6-9 5 0 18t17 7zm42 42q8-8-4-19-12-12-20-3-9 8 4 19 12 12 20 3zm57 25q3-11-13-16-15-4-19 7t13 15q15 6 19-6zm63 5q0-13-17-11-16 0-16 11 0 13 17 11 16 0 16-11zm58-10q-2-11-18-9-16 3-14 15t18 8 14-14z"></path>
  </svg>
);

const GoogleLogo = ({ size = 20, className = "" }) => (
  <svg
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 48 48"
    className={className}
    style={{ display: "block" }}
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    ></path>
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    ></path>
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    ></path>
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    ></path>
    <path fill="none" d="M0 0h48v48H0z"></path>
  </svg>
);

const LinkedinLogo = ({ size = 20, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="currentColor"
    className={className}
    viewBox="0 0 24 24"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

type Props = {
  lastUsedProvider?: string;
};

const providerConfig = {
  github: {
    icon: GithubLogo,
    label: "GitHub",
    colors: {
      bg: "bg-[#0D1117]",
      hover: "hover:bg-[#21262d]",
      focus: "focus:ring-[#21262d]/50",
      shadow: "shadow-[#0D1117]/20",
      text: "text-white",
    },
  },
  google: {
    icon: GoogleLogo,
    label: "Google",
    colors: {
      bg: "bg-gradient-to-r from-white to-gray-50",
      hover: "hover:from-gray-50 hover:to-gray-100",
      focus: "focus:ring-blue-500/30",
      shadow: "shadow-md shadow-gray-300/25",
      text: "text-gray-800",
    },
  },
  linkedin: {
    icon: LinkedinLogo,
    label: "LinkedIn",
    colors: {
      bg: "bg-[#0A66C2]",
      hover: "hover:bg-[#004182]",
      focus: "focus:ring-[#0A66C2]/50",
      shadow: "shadow-[#0A66C2]/20",
      text: "text-white",
    },
  },
  openid: {
    icon: Fingerprint,
    label: import.meta.env.VITE_OPENID_NAME || "OpenID",
    colors: {
      bg: "bg-gradient-to-r from-purple-600 to-blue-600",
      hover: "hover:from-purple-700 hover:to-blue-700",
      focus: "focus:ring-purple-500/50",
      shadow: "shadow-purple-500/20",
      text: "text-white",
    },
  },
} as const;

export const SocialAuth = ({ lastUsedProvider }: Props) => {
  const { providers } = useAuthProviders();

  if (!providers || providers.length === 0) return null;

  const getProviderButton = (provider: keyof typeof providerConfig, href: string) => {
    const config = providerConfig[provider];
    const Icon = config.icon;
    const isLastUsed = lastUsedProvider === provider;
    const isGoogle = provider === "google";

    return (
      <div className={cn("group relative", isLastUsed && "order-first")}>
        {/* Last Used Badge */}
        {isLastUsed && (
          <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 transform">
            <div className="flex items-center gap-1.5 rounded-full border border-white/20 bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-sm">
              <CheckCircle size={12} weight="fill" className="text-white/90" />
              <span>{t`Recently Used`}</span>
            </div>
          </div>
        )}

        <Button
          asChild
          size="lg"
          className={cn(
            // Base styles
            "relative h-20 w-full overflow-hidden transition-all duration-300 ease-out",
            "border-2 border-transparent backdrop-blur-sm",
            "transform hover:scale-[1.02] active:scale-[0.98]",

            // Provider-specific colors
            config.colors.bg,
            config.colors.hover,
            config.colors.focus,

            // Text color
            config.colors.text,

            // Shadow and focus
            `shadow-lg ${config.colors.shadow}`,
            "focus:ring-2 focus:ring-offset-2 focus:outline-none",
            config.colors.focus,

            // Last used styling
            isLastUsed && [
              "ring-2 ring-emerald-400/40 ring-offset-2",
              "shadow-xl shadow-emerald-500/10",
              "mt-4",
            ],

            // Google-specific border
            isGoogle && "border-gray-300 shadow-lg hover:border-gray-400",
          )}
        >
          <a
            href={href}
            className="group flex h-full w-full items-center justify-center gap-3 px-6 py-2"
            aria-label={t`Sign in with ${config.label}`}
          >
            {/* Icon with animation */}
            <Icon
              size={20}
              className={cn(
                "transition-transform duration-300 group-hover:scale-110",
                config.colors.text.replace("-700", "-600"),
              )}
            />

            {/* Label */}
            <span className={cn("text-sm font-semibold tracking-wide", config.colors.text)}>
              {config.label}
            </span>

            {/* Subtle shine effect */}
            <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
              <div className="absolute inset-0 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            </div>
          </a>
        </Button>
      </div>
    );
  };

  // Sort providers to show last used first
  const sortedProviders = providers.sort((a, b) => {
    if (a === lastUsedProvider) return -1;
    if (b === lastUsedProvider) return 1;
    return 0;
  });

  return (
    <div className="space-y-1">
      {/* Grid layout that adapts to number of providers */}
      <div className="space-y-4">
        {sortedProviders.map((provider, index) => {
          if (!(provider in providerConfig)) return null;

          const isLastUsed = lastUsedProvider === provider;
          const isLastProvider = index === sortedProviders.length - 1;

          return (
            <div key={provider}>
              <div className={cn("group relative", isLastUsed && "order-first")}>
                {/* Last Used Badge */}
                {isLastUsed && (
                  <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 transform">
                    <div className="flex items-center gap-1.5 rounded-full border border-white/20 bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-sm">
                      <CheckCircle size={12} weight="fill" className="text-white/90" />
                      <span>{t`Recently Used`}</span>
                    </div>
                  </div>
                )}
                {getProviderButton(
                  provider as keyof typeof providerConfig,
                  `/auth/oauth/${provider}`,
                )}
              </div>

              {/* Horizontal separator with "or" text */}
              {!isLastProvider && (
                <div className="flex items-center justify-center py-3">
                  <div className="flex w-full items-center">
                    <div className="via-border to-border h-px flex-1 bg-gradient-to-r from-transparent"></div>
                    <div className="mx-4 flex items-center justify-center">
                      <span className="text-muted-foreground bg-background px-2 text-xs font-medium tracking-wider uppercase">
                        {t`or`}
                      </span>
                    </div>
                    <div className="from-border via-border h-px flex-1 bg-gradient-to-r to-transparent"></div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 w-full items-center justify-center text-center text-xs opacity-50">{t`Free forever • No credit card required • 14 days free trial`}</div>
    </div>
  );
};
